import type { LeadFlowAnswerFieldName, LeadFlowAnswers } from "../types";

import {
  CARE_TYPE_VALUES,
  getPersistableFieldNames,
  LEAD_FORM_CONTRACTS,
  LEAD_FORM_TYPE,
} from "@homeinstead/forms/lead-form";

const CONTRACT = LEAD_FORM_CONTRACTS[LEAD_FORM_TYPE.careSeeker];

const PERSISTABLE_FIELD_NAMES = getPersistableFieldNames(
  LEAD_FORM_TYPE.careSeeker,
);

type QueryPrefillFieldName = keyof typeof CONTRACT.queryPrefillParameters;

function getPersistableAnswers(
  answers: LeadFlowAnswers,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(answers).filter(
      ([fieldName, value]) =>
        value !== undefined && PERSISTABLE_FIELD_NAMES.has(fieldName),
    ),
  );
}

function isLeadFlowAnswerFieldName(
  fieldName: string,
): fieldName is LeadFlowAnswerFieldName {
  return Object.hasOwn(CONTRACT.submitSchema.shape, fieldName);
}

function isPersistableAnswerFieldName(
  fieldName: string,
): fieldName is LeadFlowAnswerFieldName {
  return (
    PERSISTABLE_FIELD_NAMES.has(fieldName) &&
    isLeadFlowAnswerFieldName(fieldName)
  );
}

function isQueryPrefillFieldName(
  fieldName: string,
): fieldName is QueryPrefillFieldName {
  return Object.hasOwn(CONTRACT.queryPrefillParameters, fieldName);
}

function assignParsedAnswer(
  answers: LeadFlowAnswers,
  fieldName: LeadFlowAnswerFieldName,
  value: unknown,
) {
  const result = CONTRACT.submitSchema.shape[fieldName]?.safeParse(value);
  if (!result?.success) {
    return;
  }

  Object.assign(answers, { [fieldName]: result.data });
}

function parseCareTypePrefill(values: string[]): unknown {
  const selectedValues = new Set(
    values.flatMap((value) => value.split(",")).map((value) => value.trim()),
  );
  if (!CARE_TYPE_VALUES.some((value) => selectedValues.has(value))) {
    return undefined;
  }

  return Object.fromEntries(
    CARE_TYPE_VALUES.map((value) => [value, selectedValues.has(value)]),
  );
}

/**
 * Keeps only persistable fields whose stored value still passes the contract
 * validators. Storage is never trusted: anything else is dropped.
 */
function parseStoredAnswers(
  storedAnswers: Record<string, unknown>,
): LeadFlowAnswers {
  const validAnswers: LeadFlowAnswers = {};
  for (const [fieldName, value] of Object.entries(storedAnswers)) {
    if (!isPersistableAnswerFieldName(fieldName)) {
      continue;
    }

    assignParsedAnswer(validAnswers, fieldName, value);
  }

  return validAnswers;
}

/**
 * Reads the contract's query prefill allowlist. `careType` takes its
 * values comma-separated or repeated. Each value passes the same validators
 * as submit, so an invalid value counts as missing, never as an answer.
 */
function parseQueryPrefill(
  sessionParameters: URLSearchParams,
): LeadFlowAnswers {
  const prefilledAnswers: LeadFlowAnswers = {};

  for (const fieldName of Object.keys(CONTRACT.queryPrefillParameters)) {
    if (!isQueryPrefillFieldName(fieldName)) {
      continue;
    }

    const parameterName = CONTRACT.queryPrefillParameters[fieldName];
    const value =
      fieldName === "careType"
        ? parseCareTypePrefill(sessionParameters.getAll(parameterName))
        : (sessionParameters.get(parameterName) ?? undefined);

    if (value !== undefined) {
      assignParsedAnswer(prefilledAnswers, fieldName, value);
    }
  }

  return prefilledAnswers;
}

export { getPersistableAnswers, parseQueryPrefill, parseStoredAnswers };
